import { Component, EventEmitter, Inject, Input, OnChanges, OnDestroy, OnInit, Optional, Output } from '@angular/core';
import { Subscription } from 'rxjs';
import { AGUAdPlacement, AGUAdSessionService, AGUCrumbService, AGUToastService, DetailAdScreen, LatestRequest } from 'agu_app_shell';
import {
    ItemKind,
    ResearchItemState,
    ResearchOverlap,
    ResearchPeerReview,
    ResearchPersonProfile,
    ResearchProfileFunding,
    ResearchProfileSession,
} from '../../models/research.models';
import { ResearchApiService } from '../../services/research-api.service';
import { MemberItemStateChange, MemberItemStateService } from '../../services/member-item-state.service';
import { ExplorerRaHostAdapter } from '../../ra-explorer-host.adapter';
import { RaCompositionState } from '../../ra-composition.state';
import { RA_MODULE_KEYS } from '../../ra-module-keys';
import { DetailCrumb } from '../shared/detail-crumb';
import { DescribeError } from '../shared/describe-error';
import { LoadFailures } from '../shared/load-failures';
import { LISTING_GONE_COPY, PageErrorCopy, StatePanelCopy } from '../shared/state-copy';
import { PortalConfigService } from '../../services/portal-config.service';
import {
    FormatFundingLine,
    FormatPeerReviewLine,
    FormatSessionLine,
    FormatWorkDateLabel,
    FormatWorkMetaLine,
    ProfileWorkRow,
} from '../profile/ra-person-profile.component';
import { RecentWorksSubtitle } from '../profile/recent-works-subtitle';
import { PersonWhyItem, PersonWhyItems } from './person-card.component';

const PERSON_KIND: ItemKind = 'person';
const NO_ITEM_STATE: ResearchItemState = { saved: false, pinned: false, dismissed: false };

/** What a load renders; computed once per load, off the zoneless render path. */
interface PersonDetailView {
    readonly profile: ResearchPersonProfile | null;
    readonly workRows: ProfileWorkRow[];
    readonly recentWorksSubtitle: string;
    readonly itemState: ResearchItemState;
}

/**
 * Another member's record, opened from Collaborator Finder, on the SAME chrome
 * as Grant and Job detail (`_detail-view.scss`): the designer's 2026-10-24
 * checklist, Detail Page row 1 — "the people detail page looks like it is
 * using the Member Hub profile template (should match closer to the other
 * detail page templates)". Source of truth: `screens/detail.html`'s `expert`
 * record (:374-388) and `renderStatic()` (:484-505). The member's OWN record
 * stays on `ra-person-profile` (its own mockup, `my-profile.html`).
 *
 * `Overlaps` is the opening card's "What you share" — the profile query does
 * not carry overlaps, so the page passes the match it has in memory; on a deep
 * link with no match the section is simply absent.
 */
@Component({
    standalone: false,
    selector: 'ra-person-detail',
    templateUrl: './person-detail.component.html',
    styleUrl: './person-detail.component.scss',
})
export class PersonDetailComponent implements OnInit, OnChanges, OnDestroy {
    @Input({ required: true }) ContributorID!: string;
    /** Opened from the Recommended tab and among this member's recommendations (the cards' rule). */
    @Input() Recommended = false;
    /** The gone-listing panel's "See similar" (P/proto-states.js:121); the page navigates to its finder's All tab. */
    @Output() readonly SeeSimilar = new EventEmitter<void>();
    /** The gone-listing panel's "Back to Home" (P/proto-states.js:121); the page navigates. */
    @Output() readonly BackToHome = new EventEmitter<void>();
    @Input() Overlaps: readonly ResearchOverlap[] = [];
    /** The opening card's `isNew` — the profile wire carries none; false on a deep link, like `Overlaps`. */
    @Input() IsNew = false;
    /** The crumb trail that replaced the back pill: "{origin} / {name}" (P/detail.html:888). */
    readonly Crumb: DetailCrumb;

    profile: ResearchPersonProfile | null = null;
    loading = false;
    error: string | null = null;
    /** The error panel's foot, retry note and Contact support link (PD §5). */
    readonly failures = new LoadFailures();
    /** A record that is gone (P/proto-states.js:121) and a load that failed (state-copy.ts `PageErrorCopy`). */
    readonly GoneCopy: StatePanelCopy = LISTING_GONE_COPY;
    readonly ErrorCopy: StatePanelCopy = PageErrorCopy('this profile');
    itemState: ResearchItemState = NO_ITEM_STATE;

    /** Computed once per load, never in a getter (ui-best-practices §7). */
    workRows: ProfileWorkRow[] = [];
    recentWorksSubtitle = '';

    private stateSub: Subscription | null = null;

    /** Latest-load-wins: only the newest ContributorID's response may write state or clear `loading`. */
    private readonly request = new LatestRequest<string>();

    constructor(
        private readonly api: ResearchApiService,
        private readonly memberItemState: MemberItemStateService,
        /** The phone shell's toast, surface-provided (P/Member_Hub.html:953-958); absent outside a surface. */
        @Optional() @Inject(AGUToastService) private readonly toast: AGUToastService | null = null,
        /** The surface's host, composition and crumb service, for the crumb trail (PD §6); absent outside a surface. */
        @Optional() host: ExplorerRaHostAdapter | null = null,
        @Optional() composition: RaCompositionState | null = null,
        @Optional() @Inject(AGUCrumbService) crumbs: AGUCrumbService | null = null,
        /** The Featured placements' session state, surface-provided (PD §3); absent outside a surface. */
        @Optional() @Inject(AGUAdSessionService) private readonly ads: AGUAdSessionService | null = null,
        /** The memoised portal config, for the error panel's Contact support address (PD §5); absent outside a surface. */
        @Optional() @Inject(PortalConfigService) private readonly portalConfig: PortalConfigService | null = null
    ) {
        this.Crumb = new DetailCrumb({ ModuleKey: RA_MODULE_KEYS.People, View: 'profile', DefaultView: 'search' }, host, composition, crumbs);
    }

    /** The Circles this person is active in, after What you share — only the creative whose `personMatch` is this person's display name (P/Member_Hub.html:1629-1644). */
    get DetailCircles(): AGUAdPlacement | null {
        const name = this.profile?.name;
        if (!name) return null;
        return this.ads?.Select(DetailAdScreen(RA_MODULE_KEYS.People), 'detail-circles').find((p) => p.personMatch === name) ?? null;
    }

    ngOnInit(): void {
        this.Crumb.Start();
        void this.failures.ReadSupport(this.portalConfig);
        this.stateSub = this.memberItemState.Changes$.subscribe((change) => this.OnItemStateChanged(change));
        this.LoadIfChanged();
    }

    ngOnChanges(): void {
        // A ContributorID change while mounted (browser back/forward between two
        // collaborators) reloads; ngOnInit also calls this and HasChanged makes
        // the repeat a no-op.
        this.LoadIfChanged();
    }

    ngOnDestroy(): void {
        this.Crumb.Stop();
        this.stateSub?.unsubscribe();
    }

    private LoadIfChanged(): void {
        if (this.request.HasChanged(this.ContributorID)) {
            void this.Load();
        }
    }

    private async Load(key: string = this.ContributorID): Promise<void> {
        this.loading = true;
        this.error = null;
        this.profile = null;
        const outcome = await this.request.Run(key, (id) => this.FetchView(id));
        // A newer ContributorID owns the view (and its loading flag) now — drop this response.
        if (outcome.Status === 'stale') return;
        this.loading = false;
        if (outcome.Status === 'failed') {
            this.error = DescribeError(outcome.Error);
            this.failures.Record(outcome.Error);
            return;
        }
        this.failures.Reset();
        const view = outcome.Value;
        this.profile = view.profile;
        this.workRows = view.workRows;
        this.recentWorksSubtitle = view.recentWorksSubtitle;
        this.itemState = view.itemState;
        if (view.profile) this.Crumb.Loaded(key, view.profile.name);
    }

    private async FetchView(id: string): Promise<PersonDetailView> {
        const profile = await this.api.PersonProfile(id);
        if (!profile) return { profile: null, workRows: [], recentWorksSubtitle: '', itemState: NO_ITEM_STATE };
        const workRows = profile.recentWorks.map((w) => ({
            title: w.title,
            url: w.url,
            metaLine: FormatWorkMetaLine(w.venue, FormatWorkDateLabel(w.date)),
        }));
        return {
            profile,
            workRows,
            recentWorksSubtitle: RecentWorksSubtitle(workRows.length, profile.recentWorks[0]?.kind === 'orcid', false),
            itemState: this.memberItemState.StateOf(PERSON_KIND, id, NO_ITEM_STATE),
        };
    }

    /** The person this view shows: the key of its latest load, never the live input. */
    private get ViewedPersonID(): string | null {
        return this.request.RequestedKey;
    }

    private OnItemStateChanged(change: MemberItemStateChange): void {
        const personID = this.ViewedPersonID;
        if (change.kind !== PERSON_KIND || personID === null || change.key !== personID) return;
        this.itemState = change.itemState;
    }

    Retry(): void {
        void this.Load(this.request.RequestedKey ?? this.ContributorID);
    }

    get IsRecommended(): boolean {
        return !!this.profile && this.Recommended;
    }

    /** `.dmeta`: role · organization · department · location, null parts dropped; null when none. (Department kept from the old profile view's affiliation line, review 2026-10-24.) */
    get MetaLine(): string | null {
        if (!this.profile) return null;
        const parts = [this.profile.jobTitle, this.profile.organization, this.profile.department, this.profile.location].filter((p): p is string => !!p);
        return parts.length > 0 ? parts.join(' · ') : null;
    }

    /** Every overlap, not the card's top three — the detail is where the rest live. */
    get ShareItems(): readonly PersonWhyItem[] {
        return PersonWhyItems(this.Overlaps, Infinity);
    }

    get HasFundingService(): boolean {
        if (!this.profile) return false;
        return this.profile.funding.length > 0 || this.profile.sessionsConvened.length > 0 || !!this.profile.peerReview || this.profile.membershipSince !== null;
    }

    FundingLine(funding: ResearchProfileFunding): string {
        return FormatFundingLine(funding);
    }

    SessionLine(session: ResearchProfileSession): string {
        return FormatSessionLine(session);
    }

    PeerReviewLine(peerReview: ResearchPeerReview): string {
        return FormatPeerReviewLine(peerReview);
    }

    async OnSaveToggle(): Promise<void> {
        const personID = this.ViewedPersonID;
        if (personID === null || !this.profile) return;
        const saved = !this.itemState.saved;
        await this.memberItemState.Toggle(PERSON_KIND, personID, { saved });
        this.toast?.ShowSaved(saved); // The bookmark toast (P/Member_Hub.html:953-966, `#saveBtn` :960), once the save has stuck.
    }

    async OnPinToggle(): Promise<void> {
        const personID = this.ViewedPersonID;
        if (personID === null || !this.profile) return;
        await this.memberItemState.Toggle(PERSON_KIND, personID, { pinned: !this.itemState.pinned });
    }

    /** "Not for me": dismiss and return to wherever this was opened from. */
    async OnDismiss(): Promise<void> {
        const personID = this.ViewedPersonID;
        if (personID === null || !this.profile) return;
        await this.memberItemState.Dismiss(PERSON_KIND, personID, this.profile.name);
        this.Crumb.Back();
    }
}
